import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, AlertCircle, RefreshCw } from 'lucide-react';
import { usePageTitle } from '@/hooks/use-page-title';
import { useTeamRollup } from '@/hooks/queries/use-team-rollup';
import { useTeams } from '@/hooks/queries/use-teams';
import { cn } from '@/lib/utils';
import { getInitials } from '@/lib/get-initials';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import type { TeamRollupMember } from '@/lib/api/team-rollup';

/** Tailwind class for a progress bar fill given completion percent. */
function progressColor(percent: number): string {
  if (percent >= 100) return 'bg-emerald-400';
  if (percent >= 50) return 'bg-blue-400';
  if (percent > 0) return 'bg-amber-400';
  return 'bg-muted-foreground/30';
}

function MemberCard({ member }: { member: TeamRollupMember }) {
  const { user, progress, activeEpics, activeFeatures } = member;

  return (
    <div className="glass-card rounded-xl p-5 flex flex-col gap-4 min-w-0">
      {/* Header: identity + overall progress */}
      <div className="flex items-start gap-3 min-w-0">
        <Avatar className="h-9 w-9 shrink-0">
          {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
          <AvatarFallback className="text-sm bg-primary/10 text-primary font-semibold">
            {getInitials(user.name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight truncate">{user.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
        </div>
        <span className="text-sm font-bold tabular-nums shrink-0">
          {progress.percentComplete}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', progressColor(progress.percentComplete))}
          style={{ width: `${String(progress.percentComplete)}%` }}
        />
      </div>

      {/* Counts */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <p className="text-base font-bold tabular-nums leading-none">{progress.totalAssigned}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Assigned</p>
        </div>
        <div>
          <p className="text-base font-bold tabular-nums leading-none text-emerald-400">{progress.completed}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Done</p>
        </div>
        <div>
          <p className="text-base font-bold tabular-nums leading-none text-blue-400">{progress.inProgress}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Active</p>
        </div>
        <div>
          <p className={cn('text-base font-bold tabular-nums leading-none', progress.blocked > 0 ? 'text-red-400' : 'text-muted-foreground')}>
            {progress.blocked}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Blocked</p>
        </div>
      </div>

      {/* Active epics */}
      {activeEpics.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeEpics.map((epic) => (
            <Link
              key={epic.id}
              to={`/epics/${epic.id}`}
              className="text-[11px] px-2 py-0.5 rounded-md bg-white/[0.05] text-muted-foreground hover:text-foreground hover:bg-white/[0.08] transition-all truncate max-w-full"
              title={epic.name}
            >
              {epic.identifier ? `${epic.identifier} · ` : ''}{epic.name}
            </Link>
          ))}
        </div>
      )}

      {/* Current plan: active features */}
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Current plan</p>
        {activeFeatures.length === 0 ? (
          <p className="text-xs text-muted-foreground/70 italic">No active features</p>
        ) : (
          <ul className="space-y-1.5">
            {activeFeatures.map((feature) => (
              <li key={feature.id} className="min-w-0">
                <Link
                  to={`/features/${feature.id}`}
                  className="flex items-center gap-2 text-xs group min-w-0"
                >
                  <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                    {feature.identifier}
                  </span>
                  <span className="truncate group-hover:text-foreground transition-colors flex-1 min-w-0">
                    {feature.title}
                  </span>
                  {feature.blocked && (
                    <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">
                      Blocked
                    </span>
                  )}
                  {feature.status && !feature.blocked && (
                    <span className="shrink-0 text-[10px] text-muted-foreground">{feature.status}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function TeamRollupPage() {
  usePageTitle('Team');
  const [teamId, setTeamId] = useState<string>('');

  const { data, isLoading, error, refetch, isFetching } = useTeamRollup(teamId || undefined);
  const { data: teamsData } = useTeams();
  const teams = teamsData?.data ?? [];

  const members = data?.members ?? [];

  return (
    <div className="flex flex-col gap-5 pt-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Users className="h-[18px] w-[18px]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold leading-tight">Team</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Plan vs progress across {data ? String(data.memberCount) : '…'}{' '}
              {data?.memberCount === 1 ? 'person' : 'people'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {teams.length > 0 && (
            <select
              value={teamId}
              onChange={(e) => { setTeamId(e.target.value); }}
              aria-label="Scope by team"
              className="h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] px-3 text-xs text-foreground outline-none hover:bg-white/[0.08] transition-all"
            >
              <option value="">Whole organization</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => { void refetch(); }}
            disabled={isFetching}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && !data && (
        <div className="glass-card rounded-2xl p-8 md:p-12 text-center">
          <AlertCircle className="mx-auto mb-4 h-14 w-14 text-muted-foreground/20" />
          <p className="text-sm font-medium text-foreground">Unable to load the team rollup</p>
          <p className="mx-auto mt-1.5 max-w-[280px] text-xs leading-relaxed text-muted-foreground">
            We couldn&apos;t load your team&apos;s plan and progress. Please try again.
          </p>
          <Button className="mt-5" size="sm" variant="outline" onClick={() => { void refetch(); }}>
            Try again
          </Button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="glass-card rounded-xl p-5 h-56 animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && members.length === 0 && (
        <div className="glass-card rounded-2xl p-8 md:p-12 text-center">
          <Users className="mx-auto mb-4 h-14 w-14 text-muted-foreground/20" />
          <p className="text-sm font-medium text-foreground">No team members to show</p>
          <p className="mx-auto mt-1.5 max-w-[280px] text-xs leading-relaxed text-muted-foreground">
            There are no members in this scope, or you don&apos;t have visibility into anyone yet.
          </p>
        </div>
      )}

      {/* Rollup grid */}
      {!isLoading && members.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {members.map((member) => (
            <MemberCard key={member.user.id} member={member} />
          ))}
        </div>
      )}
    </div>
  );
}
